/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { DeliveryOption, OrgUnitContact, OrgUnitLocation, StoreHours } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, IPayLoad, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { PickUpOptionList } from '../pickup-option-list';
import { buildStoreHours, IHoursDisplayInfo, secondsToTime } from './date-time-helpers';
// @ts-ignore
import { Primary } from '@churchofjesuschrist/eden-buttons';

import styled from 'styled-components';

/**
 * Store Locator line item resources.
 */
export interface IStoreSelectorLocationLineItemResources {
    contactInfoHeader: string;
    storeHoursHeader: string;
    availabilityInfoHeader: string;

    closedText: string;

    outOfStock: string;
    inStock: string;

    currentLocation: string;
    selectLocation: string;
    selectLocationAriaLabelFormat: string;
    setAsPreferredStoreText: string;
    setAsPreferredStoreTextAriaLabel: string;
    preferredStoreText: string;
    preferredStoreAriaLabel: string;
    bopisStoreHeading: string;
    timeAm: string;
    timePm: string;

    days: {
        monday: string;
        tuesday: string;
        wednesday: string;
        thursday: string;
        friday: string;
        saturday: string;
        sunday: string;
        mondayFull: string;
        tuesdayFull: string;
        wednesdayFull: string;
        thursdayFull: string;
        fridayFull: string;
        saturdayFull: string;
        sundayFull: string;
    };
}

/**
 * Store Locator line item props.
 */
export interface IStoreSelectorLocationLineItemProps {
    location: OrgUnitLocation;
    storeHours?: StoreHours;

    hideStockStatus?: boolean;
    isInStock?: boolean;
    isCurrentLocation?: boolean;

    resources: IStoreSelectorLocationLineItemResources;
    stockStatusLabel?: string;
    storeLocatorView?: boolean;
    isPreferredStoreEnabled?: boolean;
    index: string;
    preferredStoreLocationId: string | null;
    storePickUpOptionList?: DeliveryOption[];
    productPickUpOptionList?: DeliveryOption[];
    filteredPickupMode?: string;
    shouldShowIndex?: boolean;

    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;
    preferredDistanceUnit: string;
    isLocationDisabled?: boolean;
    handlers: {
        onSelected(location: OrgUnitLocation, deliveryMode?: string): void;
        onSetAsPreferredStore(locationId: string | undefined): void;
        onRemovePreferredStore(): void;
    };
}

/**
 * Store Locator line item action component.
 */
const storeSelectorLocationLineItemComponentActions = {
    onLocationSelected(props: IStoreSelectorLocationLineItemProps, deliveryMode?: string): void {
        props.handlers.onSelected(props.location, deliveryMode);
    },
    onSetAsPreferredStore(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onSetAsPreferredStore(props.location.OrgUnitNumber);
    },
    onRemovePreferredStore(props: IStoreSelectorLocationLineItemProps): void {
        props.handlers.onRemovePreferredStore();
    }
};

/**
 * Function to render store hours.
 * @param storeHours - Store Hours object.
 * @param location - Store Unit.
 * @param resources - Resources.
 * @returns - Jsx element.
 */
const renderStoreHours = (
    storeHours: StoreHours | undefined,
    location: OrgUnitLocation,
    resources: IStoreSelectorLocationLineItemResources
): JSX.Element | null => {
    // First, use the store hours. This will allow listing day by day houts
    if (storeHours?.RegularStoreHours) {
        const storeHoursList: IHoursDisplayInfo[] = buildStoreHours(storeHours.RegularStoreHours, resources, location);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {storeHoursList.map(displayTime => {
                    return (
                        <div
                            key={JSON.stringify(displayTime)}
                            style={{ display: 'flex', justifyContent: 'space-evenly', padding: '5px', flexDirection: 'row' }}
                        >
                            {displayTime.endDayShort ? (
                                <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px' }}>
                                    <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr> –{' '}
                                    <abbr title={displayTime.endDayFull}>{displayTime.endDayShort}</abbr>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px' }}>
                                    <abbr title={displayTime.startDayFull}>{displayTime.startDayShort}</abbr>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>{displayTime.timeRange}</div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // If that fails, fall back to using the OpenFrom and OpenTo properties on the location
    const storeOpenFrom: string | undefined = secondsToTime(location.OpenFrom, resources);
    const storeOpenTo: string | undefined = secondsToTime(location.OpenTo, resources);

    return (
        <div className='ms-store-select__store-hours-details'>
            {storeOpenFrom}-{storeOpenTo}
        </div>
    );
};

/**
 * Function to retrun distance measure unit.
 * @param distance - Store location within radius.
 * @param preferredDistanceUnit - Unit configuration.
 * @param isLocationDisabled - Flag to check user location.
 * @returns - Unit of distance.
 */
export const buildDistanceString = (
    distance: number | undefined,
    preferredDistanceUnit: string,
    isLocationDisabled?: boolean
): string | undefined => {
    if (distance === undefined || isLocationDisabled) {
        return undefined;
    } else if (distance < 1) {
        return `(<1 ${preferredDistanceUnit})`;
    }
    return `(${Math.floor(distance)} ${preferredDistanceUnit})`;
};

/**
 * Function to store contact.
 * @param contacts - Store location within radius.
 * @returns - Return primary contact number of store.
 */
const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
    if (!contacts) {
        return undefined;
    }

    const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === 1 && !contact.IsPrivate);

    if (ArrayExtensions.hasElements(allPhoneContacts)) {
        const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);

        if (primaryPhoneContact) {
            return primaryPhoneContact.Locator;
        }

        return allPhoneContacts[0].Locator;
    }

    return undefined;
};

/**
 * On Store locator click functionality.
 * @param props -Store locator location items.
 * @returns Store locator selected location line item action.
 */
const onPickupInStoreClickHandler = (props: IStoreSelectorLocationLineItemProps) => () => {
    storeSelectorLocationLineItemComponentActions.onLocationSelected(props);
};

/**
 * On Store locator click functionality.
 * @param props -Store locator location items.
 * @param pickupMode -Store locator location items.
 * @returns Store locator selected location line item action.
 */
const onPickupModeSelectHandler = (props: IStoreSelectorLocationLineItemProps, pickupMode?: string) => () => {
    storeSelectorLocationLineItemComponentActions.onLocationSelected(props, pickupMode);
};

/**
 * On Change functionality.
 * @param props -Store locator location items.
 * @returns Store locator selected location line item action.
 */
const onChangeHandler = (props: IStoreSelectorLocationLineItemProps) => async (deliveryCode: string): Promise<void> => {
    storeSelectorLocationLineItemComponentActions.onLocationSelected(props, deliveryCode);
    storeSelectorLocationLineItemComponentActions.onSetAsPreferredStore(props);
    return Promise.resolve();
};

/**
 * Method to render pickup modes.
 * @param props - StoreSelector Line item props.
 * @param deliveryOptionList - Product DeliveryOption List.
 * @param pickupInStoreButtonText - Pickup store button text.
 * @param pickupInStoreAriaLabel - Pickup store aria label.
 * @returns Jsx component.
 */
const renderPickupModes = (
    props: IStoreSelectorLocationLineItemProps,
    deliveryOptionList: DeliveryOption[],
    pickupInStoreButtonText: string,
    pickupInStoreAriaLabel: string
): JSX.Element | null => {
    if (props.filteredPickupMode === undefined) {
        return (
            <PickUpOptionList
                pickupListOptionMenuText={pickupInStoreButtonText}
                pickupmodeList={deliveryOptionList}
                onChange={onChangeHandler(props)}
            />
        );
    }
    return (
        <button
            // className='ms-store-select__location-line-select-store'
            aria-label={pickupInStoreAriaLabel}
            onClick={onPickupModeSelectHandler(props, props.filteredPickupMode)}
        >
            {pickupInStoreButtonText}
        </button>
    );
};

/**
 * Method to check if we have atleast one common delivery code between product and channel.
 * @param productdeliveryOptions - Product DeliveryOption List.
 * @param storePickUpOptionList - Channel DeliveryOption List.
 * @returns DeliveryOptionList.
 */
export const matchDeliveryOptions = (
    productdeliveryOptions: DeliveryOption[] | undefined,
    storePickUpOptionList: DeliveryOption[] | undefined
): DeliveryOption[] => {
    const deliveryOption: DeliveryOption[] = [];
    productdeliveryOptions?.map(delivery => {
        const pickup = storePickUpOptionList?.find(_delivery => _delivery.Code === delivery.Code);
        if (pickup) {
            deliveryOption.push(pickup);
        }
    });

    return deliveryOption;
};

/**
 * Renders store location line items.
 * @param props - Store location line items props.
 * @returns - HTML.
 */
const storeSelectorLocationLineItem: React.FC<IStoreSelectorLocationLineItemProps> = (props: IStoreSelectorLocationLineItemProps) => {
    const {
        hideStockStatus,
        isCurrentLocation,
        isInStock,
        location,
        resources,
        storeHours,
        stockStatusLabel,
        storeLocatorView,
        telemetryContent,
        storePickUpOptionList,
        productPickUpOptionList
    } = props;

    const storeName: string | undefined = location.OrgUnitName;
    const storeAddress: string | undefined = location.Address;
    const phoneNumber: string | undefined = extractPhoneNumber(location.Contacts);

    const resourceLabel = isInStock ? resources.inStock : resources.outOfStock;
    const stockLabel = stockStatusLabel ? stockStatusLabel : undefined;
    const stockText: string | undefined = hideStockStatus ? undefined : stockLabel ? stockLabel : resourceLabel;
    const canSelectLocation = hideStockStatus || isInStock;
    const pickupInStoreButtonText: string = resources.selectLocation;
    const pickupInStoreAriaLabel: string = resources.selectLocationAriaLabelFormat.replace('{StoreName}', storeName || '');
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    payLoad.contentAction.etext = TelemetryConstant.PreferredStore;
    payLoad.contentAction.etext = TelemetryConstant.RemovePreferredStore;
    const deliveryOptionList = matchDeliveryOptions(productPickUpOptionList, storePickUpOptionList);

    const StoreSelectorLineItems = styled.div`
        display: flex;
        flex-direction: column;
    `;

    const StoreNameAndHours = styled.div`
        display: flex;
        justify-content: space-around;
        align-items: flex-start;
        margin-bottom: 20px;

        @media (max-width: 520px) {
            flex-direction: column;
        }
    `;

    const StoreAddress = styled.div`
        width: 50%;
        display: flex;
        flex-direction: column;
        align-items: center;

        @media (max-width: 520px) {
            width: 100%;
        }
    `;

    const Address = styled.div`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px;
    `;

    const StoreHours = styled.div`
        width: 50%;
        display: flex;
        flex-direction: column;

        @media (max-width: 520px) {
            width: 100%;
        }
    `;

    const StoreHoursHeader = styled.p`
        display: flex;
        justify-content: center;
    `;

    const StoreAvailability = styled.div`
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-around;

        @media (max-width: 520px) {
            flex-direction: column;
            align-items: start;
        }
    `;

    const StoreInStock = styled.div`
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    `;

    return (
        <StoreSelectorLineItems>
            <StoreNameAndHours>
                <StoreAddress>
                    <p>{resources.bopisStoreHeading}</p>
                    <span>{storeName}</span>
                    {storeAddress && (
                        <Address>
                            <span />
                            <span>{location.Street}</span>
                            <span>{`${location.City} ${location.State}, ${location.Zip}`}</span>
                        </Address>
                    )}
                    {phoneNumber && (
                        <div>
                            <span />
                            <span>{phoneNumber}</span>
                        </div>
                    )}
                </StoreAddress>
                <StoreHours>
                    <StoreHoursHeader>{resources.storeHoursHeader}</StoreHoursHeader>
                    {renderStoreHours(storeHours, location, resources)}
                </StoreHours>
            </StoreNameAndHours>
            {!storeLocatorView && (
                <StoreAvailability>
                    <StoreInStock>
                        {/* <InStockText>{resources.availabilityInfoHeader}:</InStockText> */}
                        {stockText && <p>{stockText}</p>}
                    </StoreInStock>
                    {isCurrentLocation ? (
                        <p>{resources.currentLocation}</p>
                    ) : canSelectLocation ? (
                        deliveryOptionList.length === 0 ? (
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Primary
                                    aria-label={pickupInStoreAriaLabel}
                                    onClick={onPickupInStoreClickHandler(props)}
                                    className='pickup-button'
                                    // {...attributes}
                                >
                                    {pickupInStoreButtonText}
                                </Primary>
                            </div>
                        ) : (
                            renderPickupModes(props, deliveryOptionList, pickupInStoreButtonText, pickupInStoreAriaLabel)
                        )
                    ) : (
                        false
                    )}
                </StoreAvailability>
            )}
        </StoreSelectorLineItems>
    );
};

/**
 * Store location line item component.
 */
export const StoreSelectorLocationLineItemComponent = storeSelectorLocationLineItem as (
    props: IStoreSelectorLocationLineItemProps
) => JSX.Element;
